import { Injectable, inject } from '@angular/core';
import type {
    CustomSpawnSimulatorSessionPersistence,
    CustomSpawnSimulatorSessionRecord,
} from '@animus/utility/web/custom-spawn-persistence';
import { map, type Observable } from 'rxjs';
import { RxDatabaseService } from './rxdb.service';

/** RxDB adapter for the URL-addressable custom spawn simulator session. */
@Injectable({ providedIn: 'root' })
export class RxDBCustomSpawnSimulatorSessionPersistence implements CustomSpawnSimulatorSessionPersistence {
    private readonly db = inject(RxDatabaseService).database;

    async load(sessionId: string): Promise<CustomSpawnSimulatorSessionRecord | null> {
        const document = await this.db['customSpawnSimulatorSessions'].findOne(sessionId).exec();

        return document ? parseSession(document.toMutableJSON()) : null;
    }

    observe(sessionId: string): Observable<CustomSpawnSimulatorSessionRecord | null> {
        return this.db['customSpawnSimulatorSessions']
            .findOne(sessionId)
            .$.pipe(map((document) => (document ? parseSession(document.toMutableJSON()) : null)));
    }

    async save(record: CustomSpawnSimulatorSessionRecord): Promise<void> {
        const document = {
            bracketId: record.bracketId,
            sessionId: record.sessionId,
            state: record.state,
            updatedAt: record.updatedAt,
        };

        await this.db['customSpawnSimulatorSessions'].incrementalUpsert(document);
    }
}

function parseSession(value: unknown): CustomSpawnSimulatorSessionRecord | null {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }

    const { _attachments, _deleted, _meta, _rev, ...record } = value as Record<string, unknown>;

    if (
        typeof record['sessionId'] !== 'string' ||
        typeof record['bracketId'] !== 'string' ||
        typeof record['updatedAt'] !== 'string' ||
        record['state'] === null ||
        typeof record['state'] !== 'object' ||
        Array.isArray(record['state'])
    ) {
        return null;
    }

    return record as unknown as CustomSpawnSimulatorSessionRecord;
}
